import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  ShoppingBasket,
  Plane,
  CreditCard,
  ShieldCheck,
  ArrowRight,
  Bot,
  Wallet,
  Users2,
  Tv,
  HeartPulse,
  Smartphone,
  TrendingUp,
  CheckCircle2,
  Star,
} from "lucide-react";

const Stat = ({ value, label }) => (
  <div className="flex flex-col items-start">
    <div className="font-display font-bold text-3xl text-gray-900 tracking-tight">{value}</div>
    <div className="text-sm text-gray-600 mt-1">{label}</div>
  </div>
);

const Feature = ({ icon: Icon, title, body, accent = "emerald" }) => (
  <div className="border border-gray-200 rounded-2xl p-6 bg-white hover:-translate-y-1 hover:shadow-card transition-all">
    <div className={`w-11 h-11 rounded-xl border flex items-center justify-center mb-4 ${
      accent === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
      accent === "sky" ? "bg-sky-50 text-sky-700 border-sky-100" :
      "bg-amber-50 text-amber-700 border-amber-100"
    }`}>
      <Icon className="w-5 h-5" />
    </div>
    <h3 className="font-display font-bold text-lg text-gray-900">{title}</h3>
    <p className="text-sm text-gray-600 mt-2 leading-relaxed">{body}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="relative overflow-hidden gradient-hero">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50 mb-5 px-3 py-1 rounded-full">
              <Sparkles className="w-3 h-3 mr-1.5" /> AI-powered cashback for daily life
            </Badge>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-gray-900 leading-[1.05]">
              AI finds the best deals for your life,<br />
              <span className="text-emerald-700">and you earn cashback when you shop.</span>
            </h1>
            <p className="text-lg text-gray-700 mt-6 max-w-xl leading-relaxed">
              From groceries and family essentials to travel, credit cards, and subscriptions —
              LoyaltyCircle AI learns your lifestyle and recommends the smartest place to spend.
              <strong className="text-gray-900"> 50% of every affiliate commission goes back to you.</strong>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="hero-cta-signup"
                className="inline-flex items-center justify-center bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 py-3 text-sm font-semibold transition-colors"
              >
                Get $10 free + start earning <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-cta-how"
                className="inline-flex items-center justify-center bg-white border border-gray-300 hover:border-gray-400 text-gray-900 rounded-full px-6 py-3 text-sm font-semibold transition-colors"
              >
                See how it works
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-gray-600">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Free forever</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> No app to install</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Cashback on verified purchases</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-gray-200 shadow-card aspect-[4/5] bg-white">
              <img
                src="https://images.unsplash.com/photo-1753354868431-a5317771a3ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxncm9jZXJ5JTIwc2hvcHBpbmclMjBmYW1pbHl8ZW58MHx8fHwxNzc4Mzk4MTQ2fDA&ixlib=rb-4.1.0&q=85"
                alt="Family shopping with cashback"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="w-4 h-4 text-sky-600" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Ria · AI</span>
                </div>
                <p className="text-sm text-gray-800">
                  "You could save <strong>$1,840/year</strong> by switching 5 recurring purchases
                  and using a card that matches your spending."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="border-y border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <Stat value="50%" label="Of affiliate commission paid to you as cashback" />
          <Stat value="30+" label="Top merchants live across 15 categories" />
          <Stat value="$1,840" label="Average yearly savings opportunity" />
          <Stat value="0$" label="Forever free to join — no fees, no MLM" />
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">Cashback for everything</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900">
            Save on the things you already buy
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { icon: ShoppingBasket, label: "Groceries", color: "emerald" },
            { icon: Plane, label: "Travel", color: "sky" },
            { icon: CreditCard, label: "Credit Cards", color: "amber" },
            { icon: Tv, label: "Subscriptions", color: "violet" },
            { icon: ShieldCheck, label: "Insurance", color: "teal" },
            { icon: Smartphone, label: "Telecom", color: "indigo" },
            { icon: HeartPulse, label: "Wellness", color: "red" },
            { icon: Sparkles, label: "Daily Essentials", color: "amber" },
            { icon: Star, label: "Fashion", color: "pink" },
            { icon: TrendingUp, label: "Software", color: "blue" },
          ].map((c) => (
            <div
              key={c.label}
              className="border border-gray-200 rounded-2xl p-5 bg-white text-center hover:-translate-y-0.5 hover:shadow-card transition-all"
              data-testid={`category-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className={`mx-auto w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                c.color === "emerald" ? "bg-emerald-50 text-emerald-700" :
                c.color === "sky" ? "bg-sky-50 text-sky-700" :
                c.color === "amber" ? "bg-amber-50 text-amber-700" :
                c.color === "violet" ? "bg-violet-50 text-violet-700" :
                c.color === "teal" ? "bg-teal-50 text-teal-700" :
                c.color === "indigo" ? "bg-indigo-50 text-indigo-700" :
                c.color === "red" ? "bg-rose-50 text-rose-700" :
                c.color === "pink" ? "bg-pink-50 text-pink-700" :
                "bg-blue-50 text-blue-700"
              }`}>
                <c.icon className="w-5 h-5" />
              </div>
              <div className="text-sm font-semibold text-gray-900">{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY UNIQUE */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">Why LoyaltyCircle</div>
              <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900">
                More than a cashback app — your AI savings co-pilot.
              </h2>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Most cashback apps are passive. LoyaltyCircle AI actively scouts your category, compares
                merchants, and suggests smarter alternatives — so you never overpay.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              <Feature icon={Bot} accent="sky" title="Ria, your AI assistant" body="Ask about groceries, travel, credit cards, or subscriptions. Get a personalized recommendation in seconds." />
              <Feature icon={Wallet} title="Cashback wallet" body="Track pending, approved, and lifetime cashback. Cash out via bank, PayPal, or gift card." />
              <Feature icon={Users2} accent="amber" title="Family Vault" body="Share grocery lists, cashback, and family vacation savings goals — one household, one wallet." />
              <Feature icon={TrendingUp} title="AI Savings Score" body="See your 0–100 savings score and how much more you could save by switching merchants." />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-10 lg:p-16 text-center">
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight text-gray-900 max-w-2xl mx-auto leading-tight">
            Earn cashback on the next thing you would have bought anyway.
          </h2>
          <p className="text-gray-700 mt-4 max-w-xl mx-auto">
            Join free. Get a personalized AI savings score and your first deal in 60 seconds.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/signup"
              data-testid="bottom-cta-signup"
              className="inline-flex items-center justify-center bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 py-3 text-sm font-semibold transition-colors"
            >
              Create free account <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center bg-white border border-gray-300 hover:border-gray-400 text-gray-900 rounded-full px-6 py-3 text-sm font-semibold transition-colors"
            >
              I already have an account
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
